import React, { useRef, useState } from 'react';
import henceforthApi from '../utils/henceforthApi';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";

function AddVehicle() {
  const [brand, setBrand] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [storeModels, setStoreModels] = useState<Array<string>>([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleAddModel = () => {
    const trimmed = modelInput.trim();
    if (!trimmed) {
      return toast.warn('Please enter a model name');
    }
    if (storeModels.map(m => m.toLowerCase()).includes(trimmed.toLowerCase())) {
      return toast.warn('This model is already added');
    }
    setStoreModels(prev => [...prev, trimmed]);
    setModelInput("");
  };

  const handleRemoveModel = (index: number) => {
    setStoreModels(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const brandVal = brand.trim();
    if (!brandVal) {
      return toast.warn('Please enter a vehicle brand name');
    }
    if (storeModels.length === 0) {
      return toast.warn('Please add at least one model');
    }
    const upperCaseBrand = brandVal.charAt(0).toUpperCase() + brandVal.slice(1);
    const payload = {
      name: upperCaseBrand,
      models: storeModels
    };
    setSubmitting(true);
    try {
      await henceforthApi.Vehicle.add(payload);
      Swal.fire({
        title: "Vehicle Added",
        text: "Vehicle brand and models added successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/vehicles/1");
    } catch (err: any) {
      const errorMsg =
        err?.response?.body?.error_description ||
        err?.response?.body?.message ||
        "Failed to add vehicle";
      Swal.fire({
        title: "Add Vehicle Failed",
        text: errorMsg,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Page Header */}
      <div className='row font mb-3'>
        <div className='col-auto me-auto'>
          <h2 className='mb-0'>Add Vehicle Brand</h2>
        </div>
        <div className='col-auto'>
          <button
            className='btn btn-color'
            type="button"
            onClick={() => window.history.back()}
          >
            Back
          </button>
        </div>
      </div>

      {/* Form Card */}
      <div className='ibox animated fadeInRight'>
        <div className='ibox-content'>
          <form onSubmit={handleSubmit}>
            {/* Brand Name */}
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='font-weight-bold mb-1'>
                  <b>Vehicle Brand Name</b> <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className='form-control'
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder='e.g. Toyota, Honda, Ford'
                  required
                />
              </div>
            </div>

            {/* Add Models */}
            <div className='row mb-2 align-items-end'>
              <div className='col-md-5'>
                <label className='font-weight-bold mb-1'>
                  <b>Add Vehicle Model</b>
                </label>
                <input
                  type="text"
                  className='form-control'
                  value={modelInput}
                  onChange={(e) => setModelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddModel();
                    }
                  }}
                  placeholder='e.g. Corolla, Civic, Mustang'
                />
              </div>
              <div className='col-auto'>
                <button
                  className='btn btn-color'
                  type='button'
                  onClick={handleAddModel}
                  disabled={!modelInput.trim()}
                >
                  + Add Model
                </button>
              </div>
            </div>
            <small className="text-muted mb-3 d-block">Press Enter or click "+ Add Model" to add a model to the list.</small>

            {/* Model Tags */}
            {storeModels.length > 0 && (
              <div className='mb-4'>
                <label className='font-weight-bold mb-2'><b>Added Models ({storeModels.length})</b></label>
                <div className='d-flex flex-wrap gap-2'>
                  {storeModels.map((item, index) => (
                    <span
                      key={index}
                      className='badge me-1 mb-1 d-inline-flex align-items-center'
                      style={{
                        backgroundColor: '#e8f4f8',
                        color: '#333',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        border: '1px solid #c5dce9'
                      }}
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => handleRemoveModel(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          marginLeft: '6px',
                          color: '#888',
                          fontSize: '14px',
                          lineHeight: 1,
                          padding: 0,
                        }}
                        title="Remove"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {storeModels.length === 0 && (
              <div className="alert alert-warning py-2 mb-4" role="alert">
                Please add at least one model before saving.
              </div>
            )}

            {/* Submit */}
            <div className='d-flex gap-2'>
              <button
                type="submit"
                className='btn btn-color'
                disabled={submitting || storeModels.length === 0 || !brand.trim()}
              >
                {submitting ? 'Saving...' : 'Save Vehicle Brand'}
              </button>
              <button
                type="button"
                className='btn btn-white'
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default AddVehicle;
